import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Avatar,
  TextField,
  InputAdornment,
  IconButton,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Stack,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import MKBox from 'components/MKBox';
import DefaultNavbar from 'components/Navbars/DefaultNavbar';
import routes from 'routes';
import bgImage from 'assets/images/school.jpg';
import { AluminiCard } from 'components/AluminiCard/aluminiCard';
import UserApi from 'apis/UserApi';
import searchImage from 'assets/search-engine.png';

const base_URL =  process.env.REACT_APP_API_BASE_URL;
function SearchComponent({
  onSearch = () => {},
  value = "",
  onChange = () => {},
}) {
  return (
    <div className="max-w-xl w-full">
      <div className="flex items-center">
        <i className="fas fa-search absolute ml-4 text-gray-400"></i>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Search users..."
          className="w-full pl-12 pr-10 py-3 bg-white border border-gray-200 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
        />
        {value && (
          <button
            onClick={() => onChange("")}
            className="absolute right-4 text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            <i className="fas fa-times"></i>
          </button>
        )}
      </div>
    </div>
  );
}

export default function FindAlumini() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [filters, setFilters] = useState({
    name: "",
    location: "",
    yearOfPassing: "",
    profession: ""
  });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const usersPerPage = isMobile ? 4 : 8;

  // Fetch users from API
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await UserApi.getAllUsers();

      // Check for unauthorized response
      if (response?.status === 401 || response?.data?.status === 401) {
        navigate('/pages/authentication/sign-in');
        return;
      }

      const allUsers = response?.data?.data || [];
      console.log(allUsers);

      // Apply filters
      const filteredUsers = allUsers.filter(user => {
        const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
        return (
          fullName.includes(filters.name.toLowerCase()) &&
          (user.address || '').toLowerCase().includes(filters.location.toLowerCase()) &&
          (filters.yearOfPassing === "" || user.grad_year?.toString() === filters.yearOfPassing) &&
          (user.occupation || '').toLowerCase().includes(filters.profession.toLowerCase())
        );
      });

      // Calculate pagination
      const startIndex = (page - 1) * usersPerPage;
      const endIndex = startIndex + usersPerPage;
      const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

      setUsers(paginatedUsers);
      setTotalPages(Math.ceil(filteredUsers.length / usersPerPage));
    } catch (err) {
      // Check for unauthorized error in catch block
      if (err?.response?.status === 401 || err?.response?.data?.status === 401) {
        navigate('/pages/authentication/sign-in');
        return;
      }

      setError('Failed to fetch users. Please try again later.');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(1); // Reset to first page when filters change
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  return (
    <div className='m-4'>
      <br />

      <div className='m-4 md:m-10'>

        <div className='flex flex-col items-center justify-center mb-8'>
          <div className='relative w-full max-w-2xl mx-auto mb-8'>
            <img
              src={searchImage}
              alt="search"
              className='w-full h-auto object-contain transform hover:scale-105 transition-transform duration-300'
              style={{ maxHeight: '200px' }}
            />

          </div>

          <div className='flex flex-row items-center justify-center gap-4 w-full max-w-6xl'>
            <TextField
              label="Search by Name"
              value={filters.name}
              onChange={(e) => handleFilterChange('name', e.target.value)}
              fullWidth
              variant="outlined"
              size={isMobile ? "small" : "medium"}
            />
            <TextField
              label="Search by Location"
              value={filters.location}
              onChange={(e) => handleFilterChange('location', e.target.value)}
              fullWidth
              variant="outlined"
              size={isMobile ? "small" : "medium"}
            />
            <TextField
              label="Search by Profession"
              value={filters.profession}
              onChange={(e) => handleFilterChange('profession', e.target.value)}
              fullWidth
              variant="outlined"
              size={isMobile ? "small" : "medium"}
            />
            <TextField
              label="Search by Passout Year"
              value={filters.yearOfPassing}
              onChange={(e) => handleFilterChange('yearOfPassing', e.target.value)}
              fullWidth
              variant="outlined"
              type="number"
              size={isMobile ? "small" : "medium"}
            />
          </div>
        </div>

        {error && (
          <Alert severity="error" className="mt-4">
            {error}
          </Alert>
        )}

        {loading ? (
          <div className="flex justify-center items-center min-h-[200px]">
            <CircularProgress />
          </div>
        ) : (
          <>
            <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2 lg:grid-cols-4'} gap-4 md:gap-8 px-2 md:px-8 lg:px-12 mt-5`}>
              {users.map((user, index) => (
                <AluminiCard
                  key={user.id || index}
                  name={`${user.first_name} ${user.last_name}`}
                  profession={user.occupation || 'Not specified'}
                  location={user.address || 'Not specified'}
                  imageUrl={user.profile_image?`${base_URL}/get-image?objectKey=${user.profile_image}`: 'https://randomuser.me/api/portraits/men/1.jpg'}
                  alt={`${user.first_name}'s profile picture`}
                  yearOfPassing={user.grad_year}
                />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center mt-8">
                <Stack spacing={2}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size={isMobile ? "small" : "large"}
                    showFirstButton={!isMobile}
                    showLastButton={!isMobile}
                    sx={{
                      '& .MuiPaginationItem-root': {
                        fontSize: isMobile ? '0.75rem' : '1rem',
                        minWidth: isMobile ? '32px' : '40px',
                        height: isMobile ? '32px' : '40px',
                      }
                    }}
                  />
                </Stack>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
